import { getCaptureOptions } from '../game/captureOptions';
import { isSettebello } from '../game/cards';

// ===== AI Engine =====
// Decides the CPU move. It only ever asks the Game Engine what is legally
// possible (getCaptureOptions) and only uses information a human player could
// also see: the CPU's own hand and the cards on the table. It never looks at
// the opponent's hand or the deck order, so it can never cheat.

function valueOfCapturedSet(cards) {
  let score = cards.length; // more cards is good (carte majority)
  for (const c of cards) {
    if (isSettebello(c)) score += 12; // settebello is very valuable
    if (c.suit === 'denari') score += 3; // denari majority
    if (c.value === 7) score += 4; // sevens matter for primiera
    if (c.value === 6) score += 2; // sixes matter for primiera
    if (c.value === 1) score += 1; // aces matter for primiera
  }
  return score;
}

// Rough desirability of keeping a card in hand (higher = keep it).
function keepValue(card) {
  let v = 0;
  if (isSettebello(card)) v += 12;
  if (card.suit === 'denari') v += 3;
  if (card.value === 7) v += 4;
  if (card.value === 6) v += 2;
  if (card.value === 1) v += 1;
  return v;
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Build the legal capture and discard moves from visible information only.
function legalMoves(state) {
  const hand = state.hands.cpu;
  const table = state.table;
  const captureMoves = [];
  const discardMoves = [];

  for (const card of hand) {
    const options = getCaptureOptions(card, table);
    if (options.length === 0) {
      discardMoves.push({ card, captureIds: [], score: -keepValue(card) });
      continue;
    }
    for (const option of options) {
      const remainingTable = table.filter(
        (t) => !option.some((o) => o.id === t.id)
      );
      const scopa = remainingTable.length === 0;
      let score = valueOfCapturedSet([...option, card]);
      if (scopa) score += 25; // scopa is a guaranteed point
      captureMoves.push({ card, captureIds: option.map((c) => c.id), score, scopa });
    }
  }

  return { captureMoves, discardMoves };
}

// difficulty: 'easy' (naive, relaxed) | 'hard' (strategic, competitive).
// Only the OPPONENT BEHAVIOUR changes — rules, dealing, values, scoring,
// captures and scopa conditions are all decided by the Game Engine.
export function chooseCpuMove(state, difficulty = 'hard') {
  const { captureMoves, discardMoves } = legalMoves(state);

  if (difficulty === 'easy') {
    // Naive opponent: captures when it can but without optimising which set,
    // and discards a random card. Clearly weaker, more relaxed play.
    if (captureMoves.length > 0) {
      const m = pickRandom(captureMoves);
      return { cardId: m.card.id, captureIds: m.captureIds };
    }
    const d = pickRandom(discardMoves);
    return { cardId: d.card.id, captureIds: [] };
  }

  // Hard: strategic — prefer the highest-value capture (settebello, denari,
  // sevens/sixes/aces for primiera, and scope), else discard the least useful.
  if (captureMoves.length > 0) {
    captureMoves.sort((a, b) => b.score - a.score);
    return {
      cardId: captureMoves[0].card.id,
      captureIds: captureMoves[0].captureIds,
    };
  }
  discardMoves.sort((a, b) => b.score - a.score);
  return { cardId: discardMoves[0].card.id, captureIds: [] };
}
